import { DownCircleOutlined } from '@ant-design/icons'
import * as Collapsible from '@radix-ui/react-collapsible'
import { Pencil2Icon, PlusCircledIcon, TrashIcon } from '@radix-ui/react-icons'
import { Popover, Table } from '@radix-ui/themes'
import { Button, Tooltip } from 'antd'
import { convertToUnit } from 'components/form/formHelpers'
import {
  getFormFieldsForFeatureType,
  getNewJSONForFeatureType,
  getSortedFeatureList,
} from 'features/assetDatabase/utils/assetDBFeatures'
import { generateTableCellsFromFeatureLimitedFields } from 'features/assetDatabase/utils/assetDBHelpers'
import { useGeckoLocale } from 'features/locale'
import parse from 'html-react-parser'
import isNil from 'lodash/isNil'
import React, { Dispatch, ReactNode, SetStateAction } from 'react'
import styled from 'styled-components'
import {
  GeckoEditableFeature,
  GeckoFeatureModelType,
  GeckoFeaturesJSON,
  getFormFieldByDataKey,
  SortTableByField,
} from 'types'
import { UpdaterFunction } from 'types/generics/updaterFunction'

const StyledHeaderTitle = styled.div`
  color: ${({ theme }) => theme.forecast.activeText};
  font-weight: 600;
  font-size: 0.875rem;
`

const StyledDownIcon = styled(DownCircleOutlined)`
  color: ${({ theme }) => theme.forecast.activeText};
`

const StyledCollapsibleTrigger = styled(Collapsible.Trigger)`
  background-color: ${({ theme }) => theme.forecast.defaultGhostButton};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  cursor: pointer;

  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  height: 2.5rem;
  border-radius: 0.375rem;
  padding: 0 0.75rem;
  transition: all 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
    border-color: ${({ theme }) => theme.forecast.secondaryText};
  }

  &[data-state='open'] {
    border-radius: 0.375rem 0.375rem 0 0;
    svg {
      transform: rotate(180deg);
    }
  }
`

const StyledCollapsibleRoot = styled(Collapsible.Root)`
  margin: 0.5rem 0;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.375rem;
  background-color: ${({ theme }) => theme.forecast.cardGroupBackground};
  overflow: hidden;

  &[data-state='open'] {
    ${StyledCollapsibleTrigger} {
      border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
      border-radius: 0.375rem 0.375rem 0 0;
    }
  }
`

interface CustomTableRootProps {
  className?: string
  children?: ReactNode
}

const CustomTableRoot: React.FC<CustomTableRootProps> = ({
  className,
  children,
  ...props
}: CustomTableRootProps) => (
  <Table.Root className={className} {...props}>
    {children}
  </Table.Root>
)

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.forecast.cardGroupBackground};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.forecast.border};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.forecast.secondaryText};
  }
`

const StyledTableRoot = styled(CustomTableRoot)`
  background-color: transparent;
  width: 100%;
  min-width: 0;
  table-layout: auto;
`

const StyledTableMessage = styled.div`
  background-color: transparent;
  width: 100%;
  border-bottom: solid 1px ${({ theme }) => theme.forecast.border};
  padding: 0.75rem;
  cursor: default;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.875rem;

  a {
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

const StyledTableHeaderRow = styled(Table.Row)`
  border-bottom: solid 1px ${({ theme }) => theme.forecast.border};
  background-color: transparent;
`

const StyledTableRow = styled(Table.Row)<{
  $canSelect: boolean
  $isSelected: boolean
}>`
  background-color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.forecast.activeGhostButton : 'transparent'};
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
    ${({ $canSelect }) => ($canSelect ? 'cursor: pointer;' : '')}
  }
`

const HeaderCellContent = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  font-size: 0.6875rem;
`

const StyledColumnHeaderCell = styled(Table.ColumnHeaderCell)`
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  color: ${({ theme }) => theme.forecast.activeText};
  font-size: 0.6875rem;
  height: 35px;
  align-content: center;
  align-items: center;
  min-width: 90px;
  max-width: 170px;
`

const StyledButtonColumnHeaderCell = styled(Table.ColumnHeaderCell)`
  color: ${({ theme }) => theme.forecast.activeText};
  height: 35px;
  align-content: center;
  display: flex;
  width: 100%;
  justify-content: end;
  padding-right: 16px;
  align-items: center;
`

const StyledCell = styled(Table.Cell)`
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  color: ${({ theme }) => theme.forecast.activeText};
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
  max-width: 110px;
  min-width: 0;
`

const TruncatedCoordinateText = styled.span`
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledButtonCell = styled(Table.Cell)`
  padding-left: 5px;
  color: ${({ theme }) => theme.forecast.activeText};
  white-space: nowrap;
  text-overflow: ellipsis;
  width: 100%;
  justify-content: end;
  min-width: 80px;
  display: flex;
`

const StyledButton = styled(Button)`
  background-color: transparent;
  width: 32px;
  margin-right: 5px;

  & > svg {
    min-width: 16px;
  }
`

const StyledTrashButton = styled(StyledButton)`
  color: ${({ theme }) => theme.forecast.secondaryText};
  opacity: 0.7;

  &:not(:disabled):hover {
    background-color: red !important;
    color: white !important;
    opacity: 1;
  }

  &:not(:disabled) {
    &:hover svg {
      color: white;
    }

    svg {
      color: ${({ theme }) => theme.forecast.secondaryText};
    }
  }
`

const StyledPopoverContent = styled(Popover.Content)`
  border: 2px solid ${({ theme }) => theme.forecast.background4};
  background-color: ${({ theme }) => theme.forecast.background3};
  color: ${({ theme }) => theme.forecast.activeText};
  padding: 0;
  border-radius: 0.25rem;
  max-width: 300px;
  z-index: 1000;
`

const StyledComponentEditButton = styled(Button)`
  background-color: ${({ theme }) => theme.forecast.background4};
`

const parseToReactElement = (input: string): React.ReactNode => {
  // Try parsing the HTML string into React components
  try {
    const reactElement = parse(input.trim())
    return reactElement
  } catch (error) {
    console.error('Error parsing HTML string:', error)
  }
}

const isCoordinateField = (fieldName: string): boolean => {
  return fieldName.toLowerCase().includes('coordinate')
}

/**
 * Truncates a coordinate string to show only the first few coordinate pairs
 */
const truncateCoordinates = (
  coordinateString: string,
  maxPairs: number = 3
): string => {
  const coordinatePairRegex = /\[\s*-?\d+\.?\d*\s*,\s*-?\d+\.?\d*\s*\]/g
  const matches = coordinateString.match(coordinatePairRegex)

  if (!matches || matches.length <= maxPairs) {
    return coordinateString
  }

  const firstPairs = matches.slice(0, maxPairs).join(', ')
  return `${firstPairs}, ...`
}

const CellValue: React.FC<{
  value: string | number | string[] | undefined
  fieldName: string
}> = ({ value, fieldName }) => {
  if (value === undefined || value === null) {
    return <span>-</span>
  }

  const stringValue = Array.isArray(value) ? value.join(', ') : String(value)

  if (isCoordinateField(fieldName)) {
    const truncated = truncateCoordinates(stringValue, 3)
    const isTruncated = truncated !== stringValue

    if (isTruncated) {
      return (
        <Tooltip
          title={stringValue}
          placement='top'
          overlayStyle={{ maxWidth: '600px', wordBreak: 'break-word' }}
          styles={{ container: { whiteSpace: 'normal' } }}>
          <TruncatedCoordinateText>{truncated}</TruncatedCoordinateText>
        </Tooltip>
      )
    }
  }

  return <span>{stringValue}</span>
}

interface EditFeatureTableInterface {
  tableTitle: string
  featureJSONMap: Record<string, GeckoFeaturesJSON>
  fieldsToShow: string[]
  featureClass: GeckoEditableFeature
  featureType: GeckoFeatureModelType
  sortBy?: SortTableByField[]
  onFeatureEditClicked: Dispatch<SetStateAction<SelectedFeature | undefined>>
  onFeatureSelected: UpdaterFunction<string | undefined> | null
  selectedFeatureId: string | undefined
  deleteFeature: (featureID: string) => void
  disableEditing?: boolean
  tableMessage?: string
  defaultOpen?: boolean
}

// The required props to load the EditableFeatureForm
// If FeatureID and FeatureJSON are defined, we use instantiate an instance using the FeatureClass
// If FeatureID and FeatureJSON are not defined, we pass the FeatureClass directly
export interface SelectedFeature {
  featureID: string | undefined
  featureJSON: GeckoFeaturesJSON | undefined
  featureType: GeckoFeatureModelType
}

/**
 * This component is building the Feature Tables displayed while editing the rich asset model. Functionally, this
 * component allows us to visualize a list of the features (separated by type), select individual ones to edit, and
 * also add new ones.
 * @param tableTitle                The title to be displayed on the collapsable panel
 * @param featureJSONMap            An object of a single feature type, where the key is the feature ID and the value is the JSON defining that feature
 * @param fieldsToShow              A list of strings representing the FormFields key for that feature
 * @param featureClass              A GeckoEditableFeature child class that can be instantiated using the JSONs provided
 * @param sortBy                    A list of SortTableByField objects, to sort the features by in the table
 * @param onFeatureEditClicked      A callback which will be called when the add or edit feature buttons are clicked (used for Adding & Editing)
 * @param onFeatureSelected         A callback which will be called when a feature is selected on the table
 * @param deleteFeature             A callback function to delete the selected feature form the Asset Model
 * @param disableEditing            Boolean that will disable the buttons for adding or editing the listed features
 * @param tableMessage              An optional string to show at the top of a table to describe that feature if necessary
 * @constructor
 */
export const EditFeatureTable = ({
  tableTitle,
  featureJSONMap,
  fieldsToShow,
  featureClass,
  featureType,
  sortBy,
  onFeatureEditClicked,
  onFeatureSelected,
  selectedFeatureId,
  deleteFeature,
  disableEditing,
  tableMessage,
  defaultOpen = false,
}: EditFeatureTableInterface) => {
  const { measurementSystem } = useGeckoLocale()

  // Using the fields to show as keys, look at the available form fields and parse out the labels to be used as headers
  const tableColumnHeaders = fieldsToShow.map((field) => {
    const availableFormFields = getFormFieldsForFeatureType(
      getNewJSONForFeatureType(featureType)
    )
    const formField = getFormFieldByDataKey(availableFormFields, field)
    if (!formField) {
      return
    }

    const label = formField.label
    const displayUnit = convertToUnit(
      measurementSystem,
      formField.unit_magnitude
    )
    const header = `${label}${displayUnit ? ` (${displayUnit.name})` : ''}`
    return (
      <StyledColumnHeaderCell key={label}>
        <Tooltip title={header} placement='top'>
          <HeaderCellContent>{header}</HeaderCellContent>
        </Tooltip>
      </StyledColumnHeaderCell>
    )
  })

  // Sort the list of items, allowing for an arbitrary number of sorting priorities
  const sortedFeatureList = getSortedFeatureList(featureJSONMap, sortBy)

  return (
    <StyledCollapsibleRoot key={featureType} defaultOpen={defaultOpen}>
      <StyledCollapsibleTrigger>
        <StyledHeaderTitle>{tableTitle}</StyledHeaderTitle>
        <StyledDownIcon aria-hidden='true' aria-label='' alt='' />
      </StyledCollapsibleTrigger>
      <Collapsible.Content style={{ width: '100%', boxSizing: 'border-box' }}>
        {tableMessage ? (
          <StyledTableMessage>
            <span>{parseToReactElement(tableMessage)}</span>
          </StyledTableMessage>
        ) : undefined}
        <TableWrapper>
          <StyledTableRoot>
            <Table.Header>
              <StyledTableHeaderRow>
                {tableColumnHeaders}
                <StyledButtonColumnHeaderCell>
                  <StyledButton disabled={disableEditing}>
                    <PlusCircledIcon
                      data-testid={featureClass.id}
                      onClick={() => {
                        onFeatureEditClicked({
                          featureID: undefined,
                          featureJSON: undefined,
                          featureType,
                        })
                      }}
                    />
                  </StyledButton>
                </StyledButtonColumnHeaderCell>
              </StyledTableHeaderRow>
            </Table.Header>
            <Table.Body>
              {sortedFeatureList.map(
                ([featureID, featureJSON]: [string, GeckoFeaturesJSON]) => {
                  return (
                    <StyledTableRow
                      key={featureID}
                      onClick={() =>
                        onFeatureSelected?.((prev) =>
                          prev === featureID ? undefined : featureID
                        )
                      }
                      $canSelect={!isNil(onFeatureSelected)}
                      $isSelected={selectedFeatureId === featureID}>
                      {generateTableCellsFromFeatureLimitedFields(
                        [featureID, featureJSON],
                        measurementSystem,
                        convertToUnit,
                        fieldsToShow
                      ).map((cell) => (
                        <StyledCell key={cell.fieldName}>
                          <CellValue
                            value={cell.value}
                            fieldName={cell.fieldName}
                          />
                        </StyledCell>
                      ))}
                      <StyledButtonCell>
                        <StyledButton disabled={disableEditing}>
                          <Pencil2Icon
                            onClick={(e) => {
                              e.stopPropagation()
                              onFeatureSelected?.(featureID)
                              onFeatureEditClicked({
                                featureID: featureID,
                                featureJSON: featureJSON,
                                featureType: featureType,
                              })
                            }}
                          />
                        </StyledButton>
                        <Popover.Root>
                          <Popover.Trigger>
                            <StyledTrashButton disabled={disableEditing}>
                              <TrashIcon />
                            </StyledTrashButton>
                          </Popover.Trigger>
                          <StyledPopoverContent>
                            <div>
                              <div style={{ textAlign: 'center' }}>
                                Are you sure you want to delete this feature
                                from the component? This cannot be undone.
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  width: '100%',
                                  justifyContent: 'center',
                                  paddingTop: '10px',
                                  gap: '10px',
                                }}>
                                <StyledComponentEditButton
                                  onClick={() => {
                                    deleteFeature(featureID)
                                  }}>
                                  Confirm
                                </StyledComponentEditButton>
                              </div>
                            </div>
                          </StyledPopoverContent>
                        </Popover.Root>
                      </StyledButtonCell>
                    </StyledTableRow>
                  )
                }
              )}
            </Table.Body>
          </StyledTableRoot>
        </TableWrapper>
      </Collapsible.Content>
    </StyledCollapsibleRoot>
  )
}
